import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MathText from '../components/MathText';

const API = process.env.REACT_APP_API_URL || '';

const RW_DOMAINS  = ['Craft and Structure', 'Information and Ideas', 'Standard English Convention', 'Expression of Ideas'];
const MATH_DOMAINS = ['Algebra', 'Advanced Math', 'Problem-Solving and Data Analysis', 'Geometry and Trigonometry'];

// correct / total = questions right vs attempted in the last-10-games window.
// If total === 0 the skill hasn't appeared in recent games yet → show a neutral
// "unplayed" bar at 100% in light grey so it still renders.
function ProficiencyBar({ correct, total }) {
  const unplayed = total === 0;
  const pct = unplayed ? 100 : Math.round((correct / total) * 100);
  const color = unplayed
    ? 'var(--cb-gray-300)'
    : pct >= 80 ? 'var(--cb-green)'
    : pct >= 50 ? '#d97706'
    : 'var(--cb-red)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--cb-gray-200)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .5s ease' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 42, textAlign: 'right' }}>
        {unplayed ? '—' : `${correct}/${total}`}
      </span>
    </div>
  );
}

// ── Wrong answer card ──────────────────────────────────────────────────────
function WrongAnswerCard({ item, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!selected) return;
    const res = await fetch(`${API}/api/learn/practice-answer`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ questionId: item.question_id, answer: selected }),
    }).then(r => r.json());
    setResult(res);
  };

  const dismiss = async () => {
    await fetch(`${API}/api/learn/dismiss`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ questionId: item.question_id }),
    });
    onDismiss(item.question_id);
  };

  const q = item.question;

  return (
    <div style={{ borderBottom: '1px solid var(--cb-gray-200)' }}>
      {/* collapsed row */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}
      >
        <span style={{ fontSize: 13, color: result?.correct ? 'var(--cb-green)' : 'var(--cb-red)', flexShrink: 0 }}>
          {result?.correct ? '✓' : '✗'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--cb-gray-500)', marginBottom: 2 }}>
            {item.domain} · {item.skill}
            {item.times_wrong > 1 && (
              <span style={{ color: 'var(--cb-red)', marginLeft: 6, fontWeight: 600 }}>{item.times_wrong}×</span>
            )}
          </div>
          <div style={{
            fontSize: 13, color: 'var(--cb-navy-text)', lineHeight: 1.4,
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: expanded ? 'unset' : 1, WebkitBoxOrient: 'vertical',
          }}>
            <MathText text={q.question} />
          </div>
        </div>
        <span style={{ flexShrink: 0, color: 'var(--cb-gray-500)', fontSize: 11 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {/* expanded detail */}
      {expanded && (
        <div style={{ padding: '0 20px 16px', background: 'var(--cb-gray-100)' }}>
          {q.figures && q.figures.length > 0 ? (
            q.figures.map((fig, i) => (
              <img key={i} src={`/figures/${fig}`} alt={`figure ${i + 1}`}
                style={{ maxWidth: '100%', margin: '8px 0', borderRadius: 6, border: '1px solid var(--cb-gray-200)', display: 'block' }} />
            ))
          ) : q.figure ? (
            <img src={`/figures/${q.figure}`} alt="figure"
              style={{ maxWidth: '100%', margin: '10px 0', borderRadius: 6, border: '1px solid var(--cb-gray-200)', display: 'block' }} />
          ) : null}
          {q.passage && (
            <div style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--cb-gray-700)', padding: '10px 12px',
              background: 'var(--cb-white)', borderRadius: 6, border: '1px solid var(--cb-gray-200)', margin: '10px 0' }}>
              <MathText text={q.passage} />
            </div>
          )}
          <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--cb-navy-text)', margin: '10px 0 12px', lineHeight: 1.5 }}>
            <MathText text={q.question} />
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {Object.entries(q.choices).map(([letter, text]) => {
              let cls = 'choice-btn';
              if (result) {
                if (letter === result.correctAnswer) cls += ' correct';
                else if (letter === selected && !result.correct) cls += ' incorrect';
              } else if (letter === selected) cls += ' selected';
              return (
                <button key={letter} className={cls} disabled={!!result}
                  onClick={() => !result && setSelected(letter)}
                  style={{ fontSize: 13, padding: '10px 14px' }}>
                  <span className="choice-label" style={{ fontWeight: 700, marginRight: 8 }}>{letter}</span>
                  <MathText text={text} />
                </button>
              );
            })}
          </div>
          {result && (
            <div style={{
              background: result.correct ? 'var(--cb-green-light)' : 'var(--cb-red-light)',
              border: `1px solid ${result.correct ? 'var(--cb-green)' : 'var(--cb-red)'}`,
              borderRadius: 6, padding: '10px 12px', marginBottom: 10, fontSize: 13, lineHeight: 1.6,
            }}>
              <div style={{ fontWeight: 700, color: result.correct ? 'var(--cb-green)' : 'var(--cb-red)', marginBottom: 3 }}>
                {result.correct ? '✓ Correct!' : `✗ Incorrect — Answer: ${result.correctAnswer}`}
              </div>
              {result.explanation && <div style={{ color: 'var(--cb-gray-700)' }}><MathText text={result.explanation} /></div>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {!result && (
              <button className="btn btn-primary btn-sm" disabled={!selected} onClick={submit}>Check Answer</button>
            )}
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--cb-gray-500)', fontSize: 12 }} onClick={dismiss}>
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function Learn() {
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingWrong, setLoadingWrong] = useState(true);
  const [activeSection, setActiveSection] = useState('rw');

  const fetchStats = useCallback(async () => {
    try {
      const data = await fetch(`${API}/api/learn/stats`, {
        credentials: 'include',
      }).then(r => r.json());
      setStats(Array.isArray(data) ? data : []);
    } catch {}
    setLoadingStats(false);
  }, []);

  const fetchWrong = useCallback(async () => {
    try {
      const data = await fetch(`${API}/api/learn/wrong-answers`, {
        credentials: 'include',
      }).then(r => r.json());
      setWrongAnswers(Array.isArray(data) ? data : []);
    } catch {}
    setLoadingWrong(false);
  }, []);

  useEffect(() => { fetchStats(); fetchWrong(); }, [fetchStats, fetchWrong]);

  const handleDismiss = useCallback(qid => {
    setWrongAnswers(prev => prev.filter(w => w.question_id !== qid));
  }, []);

  const displayDomains = activeSection === 'rw' ? RW_DOMAINS : MATH_DOMAINS;
  const displayStats = stats.filter(d => displayDomains.includes(d.domain));
  const displayWrong = wrongAnswers.filter(w => displayDomains.includes(w.domain));

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px 60px' }}>

      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--cb-navy-text)', marginBottom: 4 }}>Learn</h1>
        <p style={{ fontSize: 13, color: 'var(--cb-gray-500)' }}>
          Proficiency reflects your accuracy across your last 10 ranked games.
        </p>
      </div>

      {/* Section toggle */}
      <div className="lb-block-toggle" style={{ marginBottom: 22, maxWidth: 300 }}>
        {[{ key: 'rw', label: 'Reading & Writing' }, { key: 'math', label: 'Math' }].map(({ key, label }) => (
          <button key={key} className={`lb-block-tab${activeSection === key ? ' active' : ''}`}
            style={{ borderRadius: 8, padding: '7px 0', fontSize: 13, fontWeight: 600 }}
            onClick={() => setActiveSection(key)}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Skill proficiency card ── */}
      <div className="lb-block" style={{ marginBottom: 28 }}>
        <div className="lb-block-header" style={{ textAlign: 'left', padding: '16px 20px 12px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span className="lb-block-title" style={{ fontSize: 15 }}>Skill Proficiency</span>
          <span style={{ fontSize: 11, color: 'var(--cb-gray-500)', fontWeight: 500 }}>Last 10 games · correct / seen</span>
        </div>

        {loadingStats ? (
          <div style={{ padding: '24px 20px', color: 'var(--cb-gray-500)', fontSize: 13 }}>Loading…</div>
        ) : (
          displayStats.map((domainData, di) => (
            <div key={domainData.domain}>
              {/* Domain label row */}
              <div style={{
                padding: '8px 20px 6px',
                background: 'var(--cb-gray-100)',
                borderTop: di === 0 ? '1px solid var(--cb-gray-200)' : 'none',
                borderBottom: '1px solid var(--cb-gray-200)',
              }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--cb-gray-500)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                  {domainData.domain}
                </span>
              </div>

              {/* Skill rows */}
              {domainData.skills.map((s, si) => (
                <div key={s.skill} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '9px 20px',
                  borderBottom: si < domainData.skills.length - 1 ? '1px solid var(--cb-gray-100)' : 'none',
                }}>
                  <div style={{ width: 170, flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cb-navy-text)', lineHeight: 1.3 }}>{s.skill}</div>
                    {s.total > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--cb-gray-500)' }}>{s.total} seen last 10 games</div>
                    )}
                  </div>
                  <ProficiencyBar correct={s.correct} total={s.total} />
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ flexShrink: 0, fontSize: 11, padding: '4px 12px' }}
                    onClick={() => navigate(`/learn/practice/${encodeURIComponent(s.skill)}`, { state: { domain: domainData.domain } })}
                  >
                    Practice
                  </button>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* ── Recent Incorrect Questions card ── */}
      <div className="lb-block">
        <div className="lb-block-header" style={{ textAlign: 'left', padding: '16px 20px 12px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="lb-block-title" style={{ fontSize: 15 }}>Recent Incorrect Questions</span>
          {displayWrong.length > 0 && (
            <span style={{ background: 'var(--cb-red-light)', color: 'var(--cb-red)',
              fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
              {displayWrong.length}
            </span>
          )}
        </div>

        {loadingWrong ? (
          <div style={{ padding: '24px 20px', color: 'var(--cb-gray-500)', fontSize: 13 }}>Loading…</div>
        ) : displayWrong.length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center', borderTop: '1px solid var(--cb-gray-200)' }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>🌟</div>
            <div style={{ fontWeight: 600, color: 'var(--cb-navy-text)', fontSize: 14, marginBottom: 4 }}>
              {wrongAnswers.length === 0 ? 'No mistakes yet!' : 'All clear in this section!'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--cb-gray-500)' }}>
              {wrongAnswers.length === 0
                ? 'Questions you miss in ranked games appear here.'
                : 'Switch sections to see those questions.'}
            </div>
          </div>
        ) : (
          <div style={{ borderTop: '1px solid var(--cb-gray-200)' }}>
            {displayWrong.map(item => (
              <WrongAnswerCard key={item.question_id} item={item} onDismiss={handleDismiss} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
